import React from 'react';
import { ToggleButtonGroup, ToggleButton, Tooltip, Box, Typography } from '@mui/material';
import { Comment as CommentIcon } from '@mui/icons-material';

type InclusionState = 'excluded' | 'included' | 'included-with-comment';

interface ResponseInclusionToggleProps {
  responseId: string;
  questionId: string;
  isResponseIncluded: boolean;
  isCommentIncluded: boolean;
  hasComment: boolean;
  onInclusionChange: (params: {
    responseId: string;
    responseIncluded: boolean;
    commentIncluded: boolean;
  }) => void;
  disabled?: boolean;
}

export const ResponseInclusionToggle: React.FC<ResponseInclusionToggleProps> = ({
  responseId,
  questionId: _questionId,
  isResponseIncluded,
  isCommentIncluded,
  hasComment,
  onInclusionChange,
  disabled = false,
}) => {
  // Determine current state based on response and comment inclusion
  const getCurrentState = (): InclusionState => {
    if (!isResponseIncluded) {
      return 'excluded';
    }
    if (hasComment && isCommentIncluded) {
      return 'included-with-comment';
    }
    return 'included';
  };

  // Handle state changes
  const handleStateChange = (_: React.MouseEvent<HTMLElement>, newState: InclusionState | null) => {
    if (!newState || disabled) return;

    // Determine new inclusion states
    let responseIncluded = false;
    let commentIncluded = false;

    switch (newState) {
      case 'excluded':
        responseIncluded = false;
        commentIncluded = false;
        break;
      case 'included':
        responseIncluded = true;
        commentIncluded = false;
        break;
      case 'included-with-comment':
        responseIncluded = true;
        commentIncluded = true;
        break;
    }

    // Single atomic call - NO setTimeout delays
    onInclusionChange({
      responseId,
      responseIncluded,
      commentIncluded,
    });
  };

  const currentState = getCurrentState();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ToggleButtonGroup
        value={currentState}
        exclusive
        onChange={handleStateChange}
        size="small"
        disabled={disabled}
        sx={{
          '& .MuiToggleButton-root': {
            border: '1px solid',
            borderColor: 'divider',
            px: 1.5,
            py: 0.5,
            minWidth: 'auto',
            '&.Mui-selected': {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            },
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&[value="excluded"].Mui-selected': {
              backgroundColor: '#f44336 !important',
              color: 'white !important',
              '&:hover': {
                backgroundColor: '#d32f2f !important',
              },
            },
          },
        }}
      >
        <Tooltip title="Exclude this response from the report">
          <ToggleButton value="excluded" aria-label="exclude response">
            <Typography variant="caption" sx={{ textTransform: 'none' }}>
              Exclude
            </Typography>
          </ToggleButton>
        </Tooltip>

        <Tooltip title="Include response but exclude comment">
          <ToggleButton value="included" aria-label="include response only">
            <Typography variant="caption" sx={{ textTransform: 'none' }}>
              Include
            </Typography>
          </ToggleButton>
        </Tooltip>

        {hasComment && (
          <Tooltip title="Include both response and comment">
            <ToggleButton value="included-with-comment" aria-label="include response and comment">
              <CommentIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption" sx={{ textTransform: 'none' }}>
                Include + Comment
              </Typography>
            </ToggleButton>
          </Tooltip>
        )}
      </ToggleButtonGroup>
    </Box>
  );
};
